import { createContext, useContext, useEffect, useState } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
} from "react-router-dom";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "./firebase";
import logo from "./assets/logo.png";

import Navbar from "./components/Navbar";

import Login from "./pages/Login";
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import Lessons from "./pages/Lessons";
import EditLesson from "./pages/EditLesson";
import Flashcards from "./pages/Flashcards";
import Announcements from "./pages/Announcements";
import Profile from "./pages/Profile";
import Users from "./pages/Users";
import UserDetails from "./pages/UserDetails";
import AddLesson from "./pages/AddLesson";
import EditFlashcards from "./pages/EditFlashcards";
import AddFlashcards from "./pages/AddFlashcards";
import AdminUsers from "./pages/AdminUsers";
import PremiumUsers from "./pages/PremiumUsers";
import QuestionnaireResponses from "./pages/QuestionnaireResponses";
import Waitlist from "./pages/Waitlist";
import BetaSignups from "./pages/BetaSignups";
import Reports from "./pages/Reports";

// Mantine
import { MantineProvider } from "@mantine/core";
import "@mantine/core/styles.css";
import { Notifications } from "@mantine/notifications";
import "@mantine/notifications/styles.css";

// ── Auth context ────────────────────────────────────────────────
// The router below is created once at module scope, so route elements read
// the current Firebase user from context instead of a captured variable.
const AuthUserContext = createContext(null);
export const useAuthUser = () => useContext(AuthUserContext);

function RootLayout() {
  const user = useAuthUser();
  return (
    <>
      {user && <Navbar />}
      <Notifications position="top-right" />
      <div className={user ? "main-content" : ""}>
        <Outlet />
      </div>
    </>
  );
}

function Protected({ children }) {
  const user = useAuthUser();
  return user ? children : <Navigate to="/login" replace />;
}

function HomeRoute() {
  const user = useAuthUser();
  return user ? <Dashboard /> : <Landing />;
}

function LoginRoute() {
  const user = useAuthUser();
  return !user ? <Login /> : <Navigate to="/" replace />;
}

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: "/", element: <HomeRoute /> },
      { path: "/login", element: <LoginRoute /> },
      { path: "/lessons", element: <Protected><Lessons /></Protected> },
      { path: "/lessons/:id/edit", element: <Protected><EditLesson /></Protected> },
      { path: "/addlesson", element: <Protected><AddLesson /></Protected> },
      { path: "/flashcards", element: <Protected><Flashcards /></Protected> },
      { path: "/flashcards/:id/edit", element: <Protected><EditFlashcards /></Protected> },
      { path: "/addflashcards", element: <Protected><AddFlashcards /></Protected> },
      { path: "/announcements", element: <Protected><Announcements /></Protected> },
      { path: "/adminusers", element: <Protected><AdminUsers /></Protected> },
      { path: "/premiumusers", element: <Protected><PremiumUsers /></Protected> },
      { path: "/questionnaire", element: <Protected><QuestionnaireResponses /></Protected> },
      { path: "/waitlist", element: <Protected><Waitlist /></Protected> },
      { path: "/betasignups", element: <Protected><BetaSignups /></Protected> },
      { path: "/reports", element: <Protected><Reports /></Protected> },
      { path: "/users", element: <Protected><Users /></Protected> },
      { path: "/users/:id", element: <Protected><UserDetails /></Protected> },
      { path: "/profile", element: <Protected><Profile /></Protected> },
      { path: "*", element: <Navigate to="/" replace /> },
    ],
  },
]);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // Prevents flash while checking auth

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setLoading(false);
        return;
      }

      // Being signed in to Firebase Auth is not enough — the CMS shares its
      // Firebase project with the mobile app, so any app user can otherwise
      // authenticate here. Only admit users whose /users/{uid} doc has
      // admin == true; sign everyone else back out.
      let isAdmin = false;
      try {
        const snap = await getDoc(doc(db, "users", firebaseUser.uid));
        isAdmin = snap.exists() && snap.data().admin === true;
      } catch {
        isAdmin = false;
      }

      if (!isAdmin) {
        await signOut(auth);
        setUser(null);
      } else {
        setUser(firebaseUser);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        background: "#2a9d8f",
        gap: 24,
        fontFamily: "'Quicksand', sans-serif",
      }}>
        <img src={logo} alt="Kubaya" style={{ width: 72, height: 72, borderRadius: 18 }} />
        <div style={{
          width: 30,
          height: 30,
          border: "3px solid rgba(255,255,255,0.3)",
          borderTop: "3px solid #ffffff",
          borderRadius: "50%",
          animation: "spin 0.8s linear infinite",
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <MantineProvider defaultColorScheme="light">
      <AuthUserContext.Provider value={user}>
        <RouterProvider router={router} />
      </AuthUserContext.Provider>
    </MantineProvider>
  );
}

export default App;
